import React, { useState, useEffect, useMemo } from 'react';
import { formatAppDateTime } from '../../services/appConfigRuntime';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduledIcon,
  PlayArrow as RunningIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  InfoOutlined as FailureDetailsIcon
} from '@mui/icons-material';

const SyncRunStatusGrid = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [currentTenantId, setCurrentTenantId] = useState(localStorage.getItem('tenantId'));
  const [sortOption, setSortOption] = useState('latest');
  const [flowNameFilter, setFlowNameFilter] = useState('');
  /** Lowercased flow UUID -> display name from GET /synctrack/master-flow */
  const [flowNameById, setFlowNameById] = useState({});
  /** Tenant ID string -> tenant name from GET /synctrack/tenants */
  const [tenantNameById, setTenantNameById] = useState({});

  const fetchFlowNameMap = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      };
      const url = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-flow`;
      const response = await fetch(url, { headers });
      if (!response.ok) {
        console.warn('SyncRunStatusGrid: master-flow list not loaded', response.status);
        return;
      }
      const flows = await response.json();
      if (!Array.isArray(flows)) return;
      const map = {};
      flows.forEach((f) => {
        const id = f.mfId ?? f.mf_id;
        const name = f.mfName ?? f.mf_name;
        if (id == null) return;
        const key = String(id).toLowerCase();
        const label = name != null && String(name).trim() ? String(name).trim() : null;
        if (label) map[key] = label;
      });
      setFlowNameById(map);
    } catch (e) {
      console.warn('SyncRunStatusGrid: could not load flow names', e);
    }
  };

  const fetchTenantNameMap = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      };
      const url = `${process.env.REACT_APP_BACKEND_URL}synctrack/tenants`;
      const response = await fetch(url, { headers });
      if (!response.ok) {
        console.warn('SyncRunStatusGrid: tenant list not loaded', response.status);
        return;
      }
      const tenants = await response.json();
      if (!Array.isArray(tenants)) return;
      const map = {};
      tenants.forEach((tenant) => {
        const id = tenant.tenantId;
        const name = tenant.tenantName;
        if (id == null) return;
        const key = String(id);
        const label = name != null && String(name).trim() ? String(name).trim() : null;
        if (label) map[key] = label;
      });
      setTenantNameById(map);
    } catch (e) {
      console.warn('SyncRunStatusGrid: could not load tenant names', e);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Get tenant ID from localStorage
      const tenantId = localStorage.getItem('tenantId');
      const url = (tenantId && tenantId !== 'ALL')
        ? `${process.env.REACT_APP_BACKEND_URL}synctrack/tenant/${tenantId}/sync-runs`
        : `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync/runs/all`;

      console.log('🔄 Fetching sync run statuses with URL:', url);
      console.log('🆔 Tenant ID from localStorage:', tenantId);
      console.log('📋 Showing data for:', tenantId === 'ALL' ? 'All Tenants' : `Tenant ${tenantId}`);

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }


      const result = await response.json();
      console.log('📊 Sync run statuses received:', result.length, 'records');
      console.log('📋 First few records:', result.slice(0, 3));

      // Log tenant IDs in the received data
      const tenantIdsInData = [...new Set(result.map(item => item.srsTenantId))];
      console.log('🏢 Tenant IDs in received data:', tenantIdsInData);
      console.log('🎯 Expected tenant ID:', tenantId);

      setData(result);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching sync run status data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlowNameMap();
    fetchTenantNameMap();
  }, []);

  useEffect(() => {
    fetchData();

    // Listen for tenant changes
    const handleTenantChange = (event) => {
      const newTenantId = event.detail?.tenantId;
      console.log('🔄 Tenant changed in grid, fetching data for tenant:', newTenantId);
      setCurrentTenantId(newTenantId);
      fetchData();
    };

    window.addEventListener('tenantChange', handleTenantChange);

    return () => {
      window.removeEventListener('tenantChange', handleTenantChange);
    };
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status) => {
    const s = status?.toUpperCase();
    switch (s) {
      case 'SCHEDULED':
        return <ScheduledIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      case 'RUNNING':
        return <RunningIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'COMPLETED':
      case 'SUCCESS':
        return <CompletedIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'FAILED':
        return <FailedIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      default:
        return <ScheduledIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;
    }
  };

  const getFailureReason = (row) => {
    const msg = row.srsErrorMessage ?? row.srs_error_message;
    const details = row.srsErrorDetails ?? row.srs_error_details;
    const remarks = row.srsRemarks ?? row.srs_remarks;
    const chunks = [msg, details, remarks]
      .map((s) => (s != null ? String(s).trim() : ''))
      .filter(Boolean);
    const unique = [...new Set(chunks)];
    return unique.join('\n\n');
  };

  const getStatusColor = (status) => {
    const s = status?.toUpperCase();
    switch (s) {
      case 'SCHEDULED':
        return 'warning';
      case 'RUNNING':
        return 'success';
      case 'COMPLETED':
      case 'SUCCESS':
        return 'success';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    return formatAppDateTime(dateTime);
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = Math.round((end - start) / 1000);

    if (duration < 60) return `${duration}s`;
    if (duration < 3600) return `${Math.round(duration / 60)}m`;
    return `${Math.round(duration / 3600)}h`;
  };

  const getRowFlowName = (row) => {
    const rawFlowName = row.flowName ?? row.flow_name;
    if (rawFlowName != null && String(rawFlowName).trim()) {
      return String(rawFlowName).trim();
    }
    const rawId = row.srsFlowId;
    const flowKey = rawId ? String(rawId).toLowerCase() : '';
    const flowName = flowKey ? flowNameById[flowKey] : null;
    return flowName || (rawId ? String(rawId) : '');
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const query = flowNameFilter.trim().toLowerCase();
    if (!query) return data;
    return data.filter((row) => getRowFlowName(row).toLowerCase().includes(query));
  }, [data, flowNameFilter, flowNameById]);

  const sortedData = useMemo(() => {
    if (!Array.isArray(filteredData)) return [];
    const rows = [...filteredData];

    const getStartTime = (row) => (row.srsStartDateTime ? new Date(row.srsStartDateTime).getTime() : 0);

    switch (sortOption) {
      case 'oldest':
        rows.sort((a, b) => getStartTime(a) - getStartTime(b));
        break;
      case 'latest':
      default:
        rows.sort((a, b) => getStartTime(b) - getStartTime(a));
        break;
    }
    return rows;
  }, [filteredData, sortOption]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={fetchData}>
            Retry
          </Button>
        }
      >
        Error loading sync run status data: {error}
      </Alert>
    );
  }

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        p: 2,
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.dark' }}>
          📊 Sync Run Status {currentTenantId === 'ALL' ? '(All Tenants)' : ''}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {lastRefresh && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Last updated: {lastRefresh.toLocaleString()}
            </Typography>
          )}
          <Tooltip title="Debug tenant data">
            <IconButton
              size="small"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const headers = {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  };

                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync/runs/all`, { headers });
                  const allData = await response.json();

                  // Group by tenant ID
                  const tenantCounts = {};
                  allData.forEach(record => {
                    const tenantId = record.srsTenantId;
                    tenantCounts[tenantId] = (tenantCounts[tenantId] || 0) + 1;
                  });

                  console.log('🔍 DEBUG: All tenant data:', allData);
                  console.log('🏢 DEBUG: Tenant distribution:', tenantCounts);
                  alert(`Debug Info:\nTotal Records: ${allData.length}\nTenant Distribution: ${JSON.stringify(tenantCounts, null, 2)}`);
                } catch (error) {
                  console.error('Debug error:', error);
                }
              }}
            >

            </IconButton>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="sync-run-filter-label">Filter</InputLabel>
            <OutlinedInput
              label="Filter"
              placeholder="Search by flow name..."
              value={flowNameFilter}
              onChange={(event) => {
                setFlowNameFilter(event.target.value);
                setPage(0);
              }}
            />
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="sync-run-sort-label">Sort</InputLabel>
            <Select
              labelId="sync-run-sort-label"
              value={sortOption}
              label="Sort"
              onChange={(event) => {
                setSortOption(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="latest">Latest run time</MenuItem>
              <MenuItem value="oldest">Oldest run time</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh data">
            <IconButton size="small" onClick={fetchData} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Flow Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Trigger</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Start Time</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>End Time</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Tenant Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={row.srsId || index} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={(row.srsStatus === 'COMPLETED' || row.srsStatus === 'success') ? 'SUCCESS' : (row.srsStatus || 'Unknown')}
                      color={getStatusColor(row.srsStatus)}
                      size="small"
                      variant="outlined"
                    />
                    {row.srsStatus?.toLowerCase() !== 'failed' && getStatusIcon(row.srsStatus)}
                    {row.srsStatus?.toLowerCase() === 'failed' && (() => {
                      const reason = getFailureReason(row);
                      return (
                        <Tooltip
                          describeChild
                          componentsProps={{
                            tooltip: {
                              sx: {
                                maxWidth: 520,
                                maxHeight: 320,
                                overflow: 'auto',
                                bgcolor: 'common.white',
                                color: 'text.primary',
                                border: '1px solid',
                                borderColor: 'primary.main',
                                boxShadow: 3,
                                p: 1.5
                              }
                            }
                          }}
                          title={
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                display: 'block',
                                color: 'text.primary'
                              }}
                            >
                              {reason || 'No failure details were recorded for this run.'}
                            </Typography>
                          }
                        >
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="View failure reason"
                            sx={{ ml: 0.25 }}
                          >
                            <FailureDetailsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                </TableCell>
                <TableCell>
                  {(() => {
                    const rawId = row.srsFlowId;
                    const flowKey = rawId ? String(rawId).toLowerCase() : '';
                    const flowName = flowKey ? flowNameById[flowKey] : null;
                    const display = flowName || rawId || 'N/A';
                    const tooltipTitle =
                      rawId && flowName && flowName !== String(rawId) ? `ID: ${rawId}` : '';
                    return (
                      <Tooltip title={tooltipTitle}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: flowName ? 'inherit' : 'monospace',
                            fontSize: '0.75rem',
                            wordBreak: 'break-word'
                          }}
                        >
                          {display}
                        </Typography>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.srsRunType || 'N/A'}
                    size="small"
                    color={row.srsRunType === 'SCHEDULED' ? 'primary' : row.srsRunType === 'MANUAL' ? 'secondary' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {formatDateTime(row.srsStartDateTime)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {formatDateTime(row.srsEndDateTime)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDuration(row.srsStartDateTime, row.srsEndDateTime)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {(() => {
                      const tenantId = row.srsTenantId != null ? String(row.srsTenantId) : '';
                      return tenantNameById[tenantId] || tenantId || 'N/A';
                    })()}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
        component="div"
        count={sortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
      />
    </Paper>
  );
};

export default SyncRunStatusGrid;
